import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { IncptIcon } from '../icons/IncptIcon';
import { useWallet } from '../../hooks/useWallet';
import { useIncptAsset } from '../../hooks/useIncptAsset';
import { formatTokenAmount } from '../../lib/format';
import { formatDecimalDisplay } from '@incpt/shared';

import {
  UNIQUE_COINS,
  coinKey,
  coinsForChain,
  type Coin,
} from '../../lib/coins';

export {
  COINS,
  SWAPPABLE_COINS,
  SENDABLE_COINS,
  UNIQUE_COINS,
  DEFAULT_COIN,
  coinKey,
  coinsForChain,
  type Coin,
  type CoinSymbol,
} from '../../lib/coins';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (coin: Coin) => void;
  title: string;
  /** Show only one row per symbol (no network — user picks network on next screen). */
  uniqueOnly?: boolean;
  /** Restrict the list to one chain (e.g. after a network step). */
  chain?: Coin['chain'];
  /** Hide coins the swap provider cannot trade. */
  swappableOnly?: boolean;
  /** Hide coins that cannot be sent yet. */
  sendableOnly?: boolean;
  /**
   * When set, only these coin keys are shown (after other filters). Used by
   * swap to intersect the catalogue with the live ChangeHero asset list.
   */
  allowedKeys?: ReadonlySet<string>;
  /** Optional override list (e.g. already live-filtered swap coins). */
  coinsOverride?: Coin[];
  /** Show a search field — on by default when the list is long. */
  searchable?: boolean;
  /** Off-chain INCPT — shown only when the caller has access (public round or allowlist). */
  includeIncpt?: boolean;
  onSelectIncpt?: () => void;
};

export function TokenSheet({
  open,
  onClose,
  onSelect,
  title,
  uniqueOnly,
  chain,
  swappableOnly,
  sendableOnly,
  allowedKeys,
  coinsOverride,
  searchable,
  includeIncpt,
  onSelectIncpt,
}: Props) {
  const { t } = useTranslation();
  const { byToken, byTokenChain } = useWallet();
  const incpt = useIncptAsset();
  const [query, setQuery] = useState('');

  const listed = coinsOverride ?? (uniqueOnly ? UNIQUE_COINS : coinsForChain(chain));
  const coins = listed
    .filter((c) => !swappableOnly || c.swappable !== false)
    .filter((c) => !sendableOnly || c.sendable !== false)
    .filter((c) => !allowedKeys || allowedKeys.has(coinKey(c)));

  const showSearch = searchable ?? coins.length > 8;
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return coins;
    return coins.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q) ||
        c.network.toLowerCase().includes(q) ||
        c.chain.toLowerCase().includes(q),
    );
  }, [coins, q]);

  const showIncpt = Boolean(includeIncpt && incpt.visible && onSelectIncpt && uniqueOnly && !q);

  const balanceFor = (c: Coin) => {
    if (uniqueOnly) return byToken[c.symbol] ?? 0;
    return byTokenChain[`${c.symbol}:${c.chain}`] ?? 0;
  };

  // INCPT sits under the stables (USDT, USDC) and above native coins.
  const stableCoins = filtered.filter((c) => c.symbol === 'USDT' || c.symbol === 'USDC');
  const otherCoins = filtered.filter((c) => c.symbol !== 'USDT' && c.symbol !== 'USDC');

  const coinRow = (c: Coin, bordered: boolean) => {
    const bal = balanceFor(c);
    return (
      <button
        key={coinKey(c)}
        type="button"
        onClick={() => {
          setQuery('');
          onSelect(c);
        }}
        className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.03] ${
          bordered ? 'border-t border-black/[0.06]' : ''
        }`}
      >
        <c.Icon size={44} />
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-medium text-ink">{c.name}</div>
          <div className="text-[13px] text-muted">
            {uniqueOnly ? c.symbol : `${c.symbol} • ${c.network}`}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[15px] font-medium tabular-nums text-ink">
            {formatTokenAmount(bal, c.symbol)}
          </div>
          <div className="text-[12px] text-muted">{c.symbol}</div>
        </div>
      </button>
    );
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => {
        setQuery('');
        onClose();
      }}
      title={title}
    >
      {showSearch ? (
        <div className="mt-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('swap.searchAsset')}
            enterKeyHint="search"
            className="h-11 w-full rounded-[14px] bg-[#f7f7f8] px-3.5 text-[15px] text-ink outline-none placeholder:text-muted"
            aria-label={t('swap.searchAsset')}
          />
        </div>
      ) : null}
      <div className={`overflow-hidden rounded-[20px] bg-[#f7f7f8] ${showSearch ? 'mt-3' : 'mt-4'}`}>
        {filtered.length === 0 && !showIncpt ? (
          <div className="p-4 text-[14px] text-muted">{t('swap.noAssetsMatch')}</div>
        ) : (
          <>
            {stableCoins.map((c, i) => coinRow(c, i > 0))}
            {showIncpt && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  onSelectIncpt?.();
                }}
                className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.03] ${
                  stableCoins.length > 0 ? 'border-t border-black/[0.06]' : ''
                }`}
              >
                <IncptIcon size={44} scale={1.2} />
                <div className="min-w-0 flex-1">
                  <div className="text-[16px] font-medium text-ink">{t('presale.coinName')}</div>
                  <div className="text-[13px] text-muted">{incpt.symbol}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[15px] font-medium tabular-nums text-ink">
                    {formatDecimalDisplay(incpt.amount)}
                  </div>
                  <div className="text-[12px] text-muted">{incpt.symbol}</div>
                </div>
              </button>
            )}
            {otherCoins.map((c, i) =>
              coinRow(c, i > 0 || stableCoins.length > 0 || showIncpt),
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}
